import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import { BookOpen, Play, Square, ChevronDown, ChevronRight, Lock } from "lucide-react";

export default function SessionLogPanel({ gameId, isGm }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [transcript, setTranscript] = useState([]);

  const load = () => {
    api.get(`/games/${gameId}/sessions`).then((r) => setSessions(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [gameId]);

  const activeSession = sessions.find((s) => !s.ended_at);

  const startSession = async () => {
    try {
      await api.post(`/games/${gameId}/sessions/start`);
      toast.success("Session started");
      load();
    } catch {
      toast.error("Could not start session");
    }
  };

  const endSession = async () => {
    try {
      await api.post(`/games/${gameId}/sessions/end`, { summary: summaryDraft });
      toast.success("Session ended and logged");
      setSummaryDraft("");
      load();
    } catch {
      toast.error("Could not end session");
    }
  };

  const toggleExpand = async (session) => {
    if (expandedId === session.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(session.id);
    const { data } = await api.get(`/games/${gameId}/sessions/${session.id}/messages`);
    setTranscript(data);
  };

  if (loading) return <div className="p-6 text-xs text-[#f1e0c5]/40 uppercase tracking-widest">Loading session log…</div>;

  return (
    <div className="flex flex-col h-full" data-testid="session-log-panel">
      {isGm && (
        <div className="p-4 border-b border-white/10 space-y-2">
          {activeSession ? (
            <>
              <p className="text-xs text-[#4d7c0f] flex items-center gap-1.5"><Play className="w-3 h-3" /> Session {activeSession.session_number} is live</p>
              <Textarea
                data-testid="session-summary-input"
                placeholder="Recap notes for this session (optional)…"
                value={summaryDraft}
                onChange={(e) => setSummaryDraft(e.target.value)}
                className="bg-black/30 border-white/10 text-sm min-h-[70px]"
              />
              <Button data-testid="end-session-button" onClick={endSession} className="w-full rounded-full gap-2 bg-[#D90429] hover:bg-[#b8031f]">
                <Square className="w-3.5 h-3.5" /> End Session
              </Button>
            </>
          ) : (
            <Button data-testid="start-session-button" onClick={startSession} className="w-full rounded-full gap-2 bg-[#D90429] hover:bg-[#b8031f]">
              <Play className="w-3.5 h-3.5" /> Start New Session
            </Button>
          )}
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {sessions.length === 0 && <p className="text-xs text-[#f1e0c5]/40">No sessions logged yet.</p>}
          {sessions.map((s) => (
            <div key={s.id} className="rounded-xl bg-black/20 border border-white/10 overflow-hidden" data-testid={`session-entry-${s.session_number}`}>
              <button className="w-full flex items-center justify-between p-3 text-left" onClick={() => toggleExpand(s)}>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm">Session {s.session_number}{!s.ended_at && <span className="text-[#4d7c0f] text-[10px] uppercase ml-2">Live</span>}</p>
                    <p className="text-[10px] text-[#f1e0c5]/40">{new Date(s.started_at).toLocaleDateString()} · {s.message_count} messages</p>
                  </div>
                </div>
                {expandedId === s.id ? <ChevronDown className="w-4 h-4 text-[#f1e0c5]/40" /> : <ChevronRight className="w-4 h-4 text-[#f1e0c5]/40" />}
              </button>
              {expandedId === s.id && (
                <div className="px-3 pb-3 space-y-2 border-t border-white/10 pt-2">
                  {s.summary && <p className="text-xs text-[#f1e0c5]/70 italic">"{s.summary}"</p>}
                  <div className="max-h-56 overflow-y-auto space-y-1">
                    {transcript.length === 0 && <p className="text-[10px] text-[#f1e0c5]/30">No messages recorded.</p>}
                    {transcript.map((m) => (
                      <p key={m.id} className="text-[11px] text-[#f1e0c5]/60">
                        <span className="text-[#D4AF37]">{m.sender_name}:</span> {m.content}
                        {m.roll_breakdown && <span className="text-[#f1e0c5]/40"> ({m.roll_breakdown.total})</span>}
                        {m.is_private && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-[#D4AF37]"><Lock className="w-2.5 h-2.5" /> Private</span>
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
